import { CheckCircle, TrendingUp, Clock, DollarSign, Users, FileText, Cable, WorkflowIcon, RefreshCcw } from "lucide-react";
import { cn } from "@/lib/utils";

const Benefits = () => {
  const benefits = [
    {
      icon: <Clock className="h-6 w-6" />,
      title: "Never lose billable hours again",
      description: "Track time effortlessly across multiple clients and projects. Our simple interface helps you capture every minute that counts."
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: "Keep clients happy and organized",
      description: "Store all client information, project history, and communications in one place. Access everything you need instantly."
    },
    {
      icon: <FileText className="h-6 w-6" />,
      title: "Customize Your Invoices",
      description: "Create beautiful, branded invoices with custom templates, professional layouts, and your company branding. Stand out with personalized invoice designs."
    },
    {
      icon: <TrendingUp className="h-6 w-6" />,
      title: "Grow your business with insights",
      description: "Understand which clients are most profitable and where you spend your time. Make decisions based on real data."
    },
    {
      icon: <RefreshCcw className="h-6 w-6" />,
      title: "Automate your business",
      description: "Track invoice due dates, scheduled invoicing, and automatically send reminders for upcoming or overdue payments.",
      comingSoon: true
    },
    {
      icon: <Cable className="h-6 w-6" />,
      title: "Integrate with your favorite tools",
      description: "Simple and easy integration with third-party tools for a fully customizable experience.",
      comingSoon: true
    }
  ];

  return (
    <section className="py-16 sm:py-24 bg-gray-50 dark:bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-900 mb-3 sm:mb-4 px-4 sm:px-0">
            Why freelancers and consultants love Contraq
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-600 max-w-2xl mx-auto px-4 sm:px-0">
            Simple tools that solve real problems. Spend less time on admin and more time doing what you love.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {benefits.map((benefit, index) => (
            <div 
              key={index}
              className={cn(
                "bg-white dark:bg-white rounded-xl p-5 sm:p-6 shadow-sm border border-gray-100 dark:border-gray-100 hover:shadow-md transition-shadow flex flex-col h-full",
                benefit.comingSoon && "opacity-75"
              )}
            >
              <div className="flex items-start space-x-3 sm:space-x-4 flex-grow">
                <div className="flex-shrink-0">
                  <div className={cn(
                    "w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 dark:bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 dark:text-blue-600",
                    benefit.comingSoon && "bg-gray-100 dark:bg-gray-100 text-gray-400 dark:text-gray-400"
                  )}>
                    <div className="w-5 h-5 sm:w-6 sm:h-6">
                      {benefit.icon}
                    </div>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-900 mb-2 leading-tight">
                    {benefit.title}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              </div>
              {benefit.comingSoon && (
                <div className="mt-4 flex justify-center">
                  <span className="bg-orange-100 dark:bg-orange-100 text-orange-700 dark:text-orange-700 text-xs font-medium px-3 py-1 rounded-full">
                    Coming Soon
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Quick comparison */}
        <div className="mt-12 sm:mt-16 max-w-3xl mx-auto">
          <div className="bg-white dark:bg-white rounded-xl p-6 sm:p-8 shadow-sm border border-gray-100 dark:border-gray-100">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-900 mb-4 sm:mb-6 text-center">
              Perfect for solo professionals and growing teams
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 text-sm">
              <div className="text-center">
                <div className="text-2xl sm:text-3xl mb-2">👤</div>
                <h4 className="font-semibold text-gray-900 dark:text-gray-900 mb-2">Solo consultants</h4>
                <p className="text-gray-600 dark:text-gray-600">Track time, manage clients, and send professional invoices</p>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl mb-2">👥</div>
                <h4 className="font-semibold text-gray-900 dark:text-gray-900 mb-2">Small teams</h4>
                <p className="text-gray-600 dark:text-gray-600">Collaborate on projects and get insights across your business</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Benefits; 